/**
 * 生成 Studio Icon
 * 直接返回原始 icon URL
 */

interface IStudioItem {
  name: string;
  sub_studio?: {
    name: string;
  };
}

interface IGenerateBadgesOptions {
  size?: number;
}

/**
 * 根据工作室名称获取 icon URL
 * @param iconUrl - 图标 URL
 * @returns 返回 icon URL
 */
export const generateStudioBadge = async (iconUrl: string): Promise<string> => {
  return Promise.resolve(iconUrl);
};

export const createRoundImage = (src: string, size = 64): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(img, 0, 0, size, size);

      resolve(canvas.toDataURL());
    };

    img.onerror = reject;
    img.src = src;
  });

/**
 * 创建带圆角的图片
 * @param src - 图片 URL
 * @param size - 图片尺寸（宽高相同）
 * @param borderRadius - 圆角半径（像素）
 * @param backgroundColor - 背景颜色
 * @returns Promise 返回 base64 格式的图片
 */
export const createRoundedImage = (
  src: string,
  size = 64,
  borderRadius = 8,
  backgroundColor = '#ffffff',
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }

      const drawRoundedRect = (
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
      ) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(
          x + width,
          y + height,
          x + width - radius,
          y + height,
        );
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      };

      ctx.fillStyle = backgroundColor;
      drawRoundedRect(0, 0, size, size, borderRadius);
      ctx.fill();

      drawRoundedRect(0, 0, size, size, borderRadius);
      ctx.clip();

      ctx.drawImage(img, 0, 0, size, size);

      resolve(canvas.toDataURL());
    };

    img.onerror = reject;
    img.src = src;
  });

/**
 * 批量生成工作室 icon
 * @param studios - 工作室数据数组
 * @param iconMapFn - 获取 icon URL 的函数
 * @param options - 配置选项
 * @returns Promise 返回 icon URL 映射
 */
export const generateStudioBadges = async (
  studios: IStudioItem[],
  iconMapFn: (name: string) => string,
  options?: IGenerateBadgesOptions,
): Promise<Record<string, string>> => {
  const badgeMap: Record<string, string> = {};
  const size = options?.size ?? 120;

  const results = await Promise.allSettled(
    studios.map(async studio => {
      const iconUrl = iconMapFn(studio.name);
      const roundIcon = await createRoundImage(iconUrl, size);
      return { name: studio.name, roundIcon };
    }),
  );

  results.forEach(result => {
    if (result.status === 'fulfilled' && result.value) {
      const { name, roundIcon } = result.value;
      badgeMap[name] = roundIcon;
    } else if (process.env.NODE_ENV === 'development') {
      console.debug('[STH] Failed to generate badge for studio:', result);
    }
  });

  // Process sub_studios
  const subStudioResults = await Promise.allSettled(
    studios
      .filter(studio => studio.sub_studio)
      .map(async studio => {
        if (!studio.sub_studio) return null;
        const { name } = studio.sub_studio;
        const iconUrl = iconMapFn(name);
        const roundIcon = await createRoundImage(iconUrl, size);
        return { name, roundIcon };
      }),
  );

  subStudioResults.forEach(result => {
    if (result.status === 'fulfilled' && result.value) {
      const { name, roundIcon } = result.value;
      badgeMap[name] = roundIcon;
    } else if (process.env.NODE_ENV === 'development') {
      console.debug('[STH] Failed to generate badge for sub-studio:', result);
    }
  });

  return badgeMap;
};
